import React, { useEffect, useState } from 'react';

import { Button, Popover, TextFieldProps } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { isValid } from 'date-fns';
import { useDebounce } from 'hooks/useDebounce';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { DateObject } from 'react-multi-date-picker';
import { Calendar } from 'shared/mui/Calendar/Calendar';
import { DatePickerActions } from 'shared/mui/DatePicker/components/DatePickerActions/DatePickerActions';
import { DatePickerMaskInput } from 'shared/mui/DatePicker/components/DatePickerMaskInput/DatePickerMaskInput';
import { DatePickerTime } from 'shared/mui/DatePicker/components/DatePickerTime/DatePickerTime';
import {
  DEFAULT_DATE_MASK,
  DEFAULT_DATE_TIME_MASK,
  DEFAULT_DATE_TIME_PICKER_MASK,
  DEFAULT_DATE_TIME_PICKER_PLACEHOLDER,
  DEFAULT_TIME_MASK,
} from 'shared/mui/DatePicker/config/defaultValues';
import {
  PopoverCalendarContainerStyled,
  PopoverContentStyled,
  PopoverDatesStyled,
  PopoverLayoutStyled,
  PopoverTimesStyled,
} from 'shared/mui/DatePicker/DatePicker.styled';
import { useAnchor } from 'shared/mui/DatePicker/hooks/useAnchor';
import { formatInputToPlaceholder } from 'shared/mui/DatePicker/utils/formatInputToPlaceholder';
import { getValidCalendarDate } from 'shared/mui/DatePicker/utils/getValidCalendarDate';
import { setCurrentTime } from 'shared/mui/DatePicker/utils/setCurrentTime';
import { setToday } from 'shared/mui/DatePicker/utils/setToday';
import { formatInputToTimestamp, parseDateInput } from 'utils/date';

export type DateRangePickerValue = number | null;

export type DateTimePickerProps = Omit<TextFieldProps, 'value' | 'onChange' | 'defaultValue'> & {
  minDate?: Date | string | number;
  maxDate?: Date | string | number;
  value: DateRangePickerValue;
  onChange?: (newValue: DateRangePickerValue) => void;
  defaultValue?: number;
  applyOnInput?: boolean;
};

export const DateTimePicker: React.FC<DateTimePickerProps> = ({
  label,
  variant = 'outlined',
  disabled,
  onChange: onChangeProp,
  minDate,
  maxDate,
  value,
  defaultValue,
  size = 'small',
  applyOnInput = false,
  ...restProps
}) => {
  const translate = useTypedTranslate();
  const { handleClose, handleClick, anchorEl } = useAnchor<HTMLDivElement>();
  const [error, setError] = useState(false);
  const [startTime, setStartTime] = useState<DateRangePickerValue>(value);

  const [resetValue, setResetValue] = useState<DateRangePickerValue>(value);

  const [inputValue, setInputValue] = useState<string | undefined>(
    formatInputToPlaceholder(value, { format: DEFAULT_DATE_TIME_MASK }),
  );

  const [calendarValue, setCalendarValue] = useState<DateRangePickerValue>(value);

  const open = Boolean(anchorEl);
  const id = open ? 'date-time-picker-popover' : undefined;

  useEffect(() => {
    setStartTime(value);
    setInputValue(formatInputToPlaceholder(value, { format: DEFAULT_DATE_TIME_MASK }));
    setCalendarValue(value);
  }, [value]);

  const debouncedOnChangeInputHandler = useDebounce((target: string) => {
    const start = target;

    if (!start) {
      setError(false);
      setStartTime(null);
      setCalendarValue(null);
      setInputValue(undefined);

      if (applyOnInput) {
        onChangeProp?.(null);
      }

      return;
    }

    const [startDate, startTime] = start.split(' ');

    setError(true);
    const validStartDate = formatInputToTimestamp(startDate, { format: DEFAULT_DATE_MASK });

    if (validStartDate) {
      const parsedTime = parseDateInput(startTime, { format: DEFAULT_TIME_MASK });

      if (!parsedTime) {
        setCalendarValue(null);
        setStartTime(null);
        return;
      }

      const newValue = setCurrentTime(validStartDate, parsedTime);

      setError(false);
      setCalendarValue(newValue);
      setStartTime(newValue);

      if (applyOnInput) {
        onChangeProp?.(newValue);
      }
    } else {
      setCalendarValue(null);
      setStartTime(null);
    }

    setInputValue(target);
  }, 300);

  function onChangeInput(event: React.ChangeEvent<HTMLInputElement>) {
    debouncedOnChangeInputHandler(event?.target?.value);
  }

  function onChangeCalendar(value: DateObject | null) {
    const validStart = getValidCalendarDate(value, startTime);

    setStartTime(validStart);

    setInputValue(formatInputToPlaceholder(validStart, { format: DEFAULT_DATE_TIME_MASK }));
    setCalendarValue(validStart);
  }

  const onChangeStartTime = (value: Date) => {
    const startDate = calendarValue;

    if (!isValid(value)) {
      setStartTime(null);
      setCalendarValue(null);
      setInputValue(formatInputToPlaceholder(null, { format: DEFAULT_DATE_TIME_MASK }));

      return;
    }

    if (!startDate) {
      const newValue = setToday(value);
      setStartTime(newValue);
      setCalendarValue(newValue);
      setInputValue(formatInputToPlaceholder(newValue, { format: DEFAULT_DATE_TIME_MASK }));

      return;
    }

    const newDateTime = setCurrentTime(startDate, value);
    setStartTime(newDateTime);
    setCalendarValue(newDateTime);
    setInputValue(formatInputToPlaceholder(newDateTime, { format: DEFAULT_DATE_TIME_MASK }));
  };

  function onClickApply() {
    if (onChangeProp) {
      onChangeProp(calendarValue);
      setResetValue(resetValue);
      handleClose();
    }
  }

  function onClickReset() {
    if (onChangeProp) {
      onChangeProp(resetValue);
      setStartTime(resetValue);
      setInputValue(formatInputToPlaceholder(resetValue, { format: DEFAULT_DATE_TIME_MASK }));
      setCalendarValue(resetValue);
      handleClose();
    }
  }

  const onResetInput = () => {
    if (onChangeProp) {
      onChangeProp(null);
      setResetValue(defaultValue || null);
      setError(false);
    }
  };

  return (
    <>
      <DatePickerMaskInput
        {...restProps}
        disabled={disabled}
        error={error || restProps?.error}
        label={label}
        mask={DEFAULT_DATE_TIME_PICKER_MASK}
        onChange={onChangeInput}
        onClick={(e) => {
          if (disabled) {
            return;
          }

          handleClick(e);
        }}
        onResetInput={onResetInput}
        placeholder={DEFAULT_DATE_TIME_PICKER_PLACEHOLDER}
        size={size}
        value={inputValue}
        variant={variant}
      />
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        disableAutoFocus={true}
        disableScrollLock={true}
        id={id}
        onClose={handleClose}
        open={open}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <PopoverLayoutStyled
          sx={{
            height: '465px',
          }}
        >
          <PopoverCalendarContainerStyled>
            <PopoverContentStyled>
              <PopoverDatesStyled>
                <PopoverCalendarContainerStyled>
                  <Calendar
                    maxDate={maxDate}
                    minDate={minDate}
                    onChange={onChangeCalendar}
                    value={calendarValue}
                  />
                </PopoverCalendarContainerStyled>
                <PopoverTimesStyled>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePickerTime
                      label={translate('app.datePicker.time')}
                      onChange={onChangeStartTime}
                      value={startTime}
                    />
                  </LocalizationProvider>
                </PopoverTimesStyled>
              </PopoverDatesStyled>
            </PopoverContentStyled>
          </PopoverCalendarContainerStyled>
          <DatePickerActions>
            <Button onClick={onClickReset} variant="outlined">
              {translate('app.buttons.cancel')}
            </Button>
            <Button onClick={onClickApply} variant="contained">
              {translate('app.buttons.confirm')}
            </Button>
          </DatePickerActions>
        </PopoverLayoutStyled>
      </Popover>
    </>
  );
};
